import { keepPreviousData } from "@tanstack/react-query";
import { getUserId } from "shared-frontend/functions/auth";

import { useGetSubscriptionList } from "../api/generated/customer/customer";

/**
 * The response as it reads before the query resolves, on error, and when there is no customer to ask
 * about.
 *
 * Frozen and shared so no consumer needs a null check: the page renders its chrome, an empty table and
 * four zero counts, which is what it did before the request resolved anyway. `bucketCounts` in
 * particular has to be present from the first frame, because the page decides between its three render
 * branches by reading it — an undefined there would show "you have no subscriptions" to everybody for a
 * moment.
 */
const EMPTY_LIST = Object.freeze( {
	items: Object.freeze( [] ),
	meta: Object.freeze( { total: 0, page: 1, limit: 25, totalPages: 0 } ),
	bucketCounts: Object.freeze( { needsAttention: 0, provisioned: 0, regular: 0, inactive: 0 } ),
} );

/**
 * Parses an API date string into a Date, preserving null.
 *
 * Null rather than `new Date( null )`, which the selector this replaces produced and which is the epoch —
 * a date 56 years in the past. Every "expired more than a month ago" test would pass on it, so a
 * subscription with no dates at all would render as long expired rather than as having no date to show.
 *
 * @param {string|null} value The ISO date string, or null.
 *
 * @returns {Date|null} The date, or null when there is none.
 */
function toDateOrNull( value ) {
	return value === null || value === undefined ? null : new Date( value );
}

/**
 * Maps a subscription row onto the props the table components read.
 *
 * The row is already presentation-shaped — the server buckets, sorts and resolves the provisioner — so
 * this bridges names and types only, and deliberately adds nothing derived that the server could answer.
 *
 * **`endDate` and `nextPayment` must be real `Date` objects.**
 * `SubscriptionDetailModal.jsx:59` does `props.subscription.nextPayment?.getTime()`, which on a string
 * yields undefined, so `Math.max` returns NaN and the "expired more than a month ago" comparison
 * silently evaluates false — the wrong copy and the wrong button, with nothing thrown and no test
 * failing. `SubscriptionRow` and `SubscriptionDetails` also pass both straight to `FormattedDate` and
 * `toLocaleDateString`.
 *
 * `hasNextPayment` / `hasEndDate` are kept because the components branch on them
 * (`SubscriptionRow.jsx:87,101,112`, `SubscriptionDetails.jsx:25`) and a Date is truthy even when it is
 * the epoch, so `Boolean( endDate )` is not the same test.
 *
 * Not mapped, all verified unread or read-but-never-projected — do not "fix" these back in:
 * `startDate` (projected by the old selector, read nowhere), `hasSites` (computed by the old selector,
 * read nowhere), `paymentDate` (the old sort key, now the server's `ORDER BY`), `lastBillingTerm` (the
 * gate on the permanently dead `futureNeedsAttention` branch), and `billingType`
 * (`SubscriptionRow.jsx:138` reads it off the subscription, but nothing ever set it there — the row
 * computes its own at `:144-147`).
 *
 * @param {Object} subscription The subscription row from the API.
 *
 * @returns {Object} The row as the components expect it.
 */
export function toRowProps( subscription ) {
	return {
		...subscription,
		endDate: toDateOrNull( subscription.endDate ),
		hasEndDate: subscription.endDate !== null,
		nextPayment: toDateOrNull( subscription.nextPayment ),
		hasNextPayment: subscription.nextPayment !== null,
	};
}

/**
 * One page of the customer's subscriptions, from `GET Customers/:id/subscriptions/list`.
 *
 * Replaces the unbounded subscription collection and the client-side pipeline that reduced it: the
 * search, the four-way split, the sort and the grouping all happen in SQL now, so the browser receives
 * one page of finished rows instead of every subscription on the account. For the largest accounts that
 * is kilobytes rather than tens of megabytes — the request that made this page unusable, and the reason
 * this hook exists.
 *
 * Deliberately holds no `useEffect`: this is a read, so there is nothing to mirror into Redux and
 * nothing to report to the data layer. Analytics belong on the mutation that caused the event, never on
 * a query result, which reflects cache state rather than something having happened.
 *
 * Each section of the overview calls this separately, with its own `bucket` and `page`. Since every
 * parameter is part of the query key, the sections cache and revalidate independently: paging one leaves
 * the others untouched, rather than invalidating a combined payload. `bucketCounts` is account-wide and
 * identical in every response, so whichever section reads it gets the same answer.
 *
 * @param {Object} params The list parameters — query, status, bucket, sort, order, page, limit.
 *
 * @returns {{items: Object[], meta: Object, bucketCounts: Object, isLoading: boolean, isError: boolean,
 *            isPlaceholderData: boolean}} The page, the counts and the query state.
 */
export default function useSubscriptionList( params = {} ) {
	const customerId = getUserId();

	const { data, isLoading, isError, isPlaceholderData } = useGetSubscriptionList( customerId, params, {
		query: {
			// `getUserId()` returns an empty string when there is no access token, and the generated
			// `enabled` only guards null and undefined — so without this the hook would request
			// `Customers//subscriptions/list`.
			enabled: Boolean( customerId ),

			// Set here rather than on the shared client. Every parameter is part of the query key, so each
			// page change and each debounced keystroke is a cache miss that would otherwise unmount the whole
			// table into a loading state and drop the user's scroll position. Keeping the previous page
			// rendered is only honest if the caller can say so, hence `isPlaceholderData` in the return.
			//
			// This does not touch freshness: `staleTime` stays 0, per the reasoning in `queryClient.ts`. It
			// addresses the flash between two resolved pages, not how long either is trusted.
			placeholderData: keepPreviousData,
		},
	} );

	const response = data ?? EMPTY_LIST;

	return {
		items: data ? data.items.map( toRowProps ) : EMPTY_LIST.items,
		meta: response.meta,
		bucketCounts: response.bucketCounts,
		isLoading,
		isError,
		isPlaceholderData,
	};
}
